// src/services/api.js
import axios from 'axios';

const CLAVE_TOKEN_ACCESO = 'token_acceso_admin';
const CLAVE_TOKEN_REFRESCO = 'token_refresco_admin';
const CLAVE_TOKEN_ACCESO_LEGADO = 'access_token';
const CLAVE_TOKEN_REFRESCO_LEGADO = 'refresh_token';

function obtenerTokenAcceso() {
  return localStorage.getItem(CLAVE_TOKEN_ACCESO) || localStorage.getItem(CLAVE_TOKEN_ACCESO_LEGADO);
}

function obtenerTokenRefresco() {
  return localStorage.getItem(CLAVE_TOKEN_REFRESCO) || localStorage.getItem(CLAVE_TOKEN_REFRESCO_LEGADO);
}

// Configuración base de la API: usar la variable de entorno definida en los .env
// Si no está definida en el entorno, usar una ruta relativa para permitir proxy durante desarrollo
const rawBaseURL = process.env.VUE_APP_API_BASE_URL || '/api';

// Normalizar la URL base para asegurar que termine con /api
let baseURL;
if (rawBaseURL.startsWith('http')) {
  // Es una URL completa, asegurar que termine con /api
  baseURL = rawBaseURL.replace(/\/$/, '') + '/api';
} else {
  // Es una ruta relativa
  baseURL = rawBaseURL;
}

const api = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Adjuntar token de acceso a cada petición
api.interceptors.request.use((config) => {
  const token = obtenerTokenAcceso();
  
  // Rutas públicas que no requieren token
  const publicRoutes = [
    // Nuevas rutas en español
    '/autenticacion/iniciar',
    '/autenticacion/refrescar',
    '/autenticacion/recuperar-clave',
    '/registro-empresas',
    '/registro-empresas/verificar-ruc',
    // Rutas legacy
    '/auth/login',
    '/auth/refresh',
    '/auth/forgot-password',
    '/companies-register',
    '/companies-register/check-ruc',
    // Público
    '/sri/ruc-catastro'
  ];
  
  if (publicRoutes.some(route => config.url.startsWith(route))) {
    return config;
  }
  
  if (!token) {
    return config;
  }

  // Verificar si el token está expirado
  if (isTokenExpired(token)) {
    console.warn('[DEBUG API Admin] Token expirado detectado, limpiando y redirigiendo al inicio de sesión');
    // Limpiar token expirado
    localStorage.removeItem(CLAVE_TOKEN_ACCESO);
    localStorage.removeItem(CLAVE_TOKEN_REFRESCO);
    localStorage.removeItem(CLAVE_TOKEN_ACCESO_LEGADO);
    localStorage.removeItem(CLAVE_TOKEN_REFRESCO_LEGADO);
    // Redirigir al inicio de sesión si no estamos allí
    if (window.location.pathname !== '/iniciar-sesion') {
      window.location.href = '/iniciar-sesion';
    }
    return Promise.reject(new Error('Token expirado'));
  }

  config.headers = {
    ...config.headers,
    'Authorization': `Bearer ${token}`
  };
  
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Handle 401 responses by attempting a token refresh once
let isRefreshing = false;
let pendingRequests = [];

function subscribeTokenRefresh(cb) { pendingRequests.push(cb); }
function onRefreshed(newToken) {
  pendingRequests.forEach((cb) => cb(newToken));
  pendingRequests = [];
}

// Verificar expiración del token antes de cada request
function isTokenExpired(token) {
  if (!token) return true;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    if (!payload.exp) return false; // Token sin exp, considerar válido
    const now = Math.floor(Date.now() / 1000);
    return payload.exp < now;
  } catch {
    return true;
  }
}

api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;

    // Do not attempt refresh for auth endpoints
    const originalUrl = original.url || '';
    const isAuthEndpoint =
      originalUrl.startsWith('/autenticacion/iniciar') ||
      originalUrl.startsWith('/autenticacion/refrescar') ||
      originalUrl.startsWith('/autenticacion/recuperar-clave') ||
      originalUrl.startsWith('/auth/login') ||
      originalUrl.startsWith('/auth/refresh') ||
      originalUrl.startsWith('/auth/forgot-password');
    if (status === 401 && !original._retry) {
      if (isAuthEndpoint) {
        return Promise.reject(error);
      }
      const refreshToken = obtenerTokenRefresco();
      if (!refreshToken) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue request until refresh completes
        return new Promise((resolve) => {
          subscribeTokenRefresh((newToken) => {
            original.headers = original.headers || {};
            original.headers.Authorization = `Bearer ${newToken}`;
            resolve(api.request(original));
          });
        });
      }

      original._retry = true;
      isRefreshing = true;

      try {
        const { data } = await api.post('/autenticacion/refrescar', { refresh_token: refreshToken });
        const newToken = data?.access_token;
        if (!newToken) throw new Error('No access_token in refresh response');

        localStorage.setItem(CLAVE_TOKEN_ACCESO, newToken);
        onRefreshed(newToken);

        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newToken}`;
        return api.request(original);
      } catch (e) {
        // Purge tokens on failure to refresh
        console.error('[DEBUG API Admin] Refresh token failed, redirecting to login:', e);
        localStorage.removeItem(CLAVE_TOKEN_ACCESO);
        localStorage.removeItem(CLAVE_TOKEN_REFRESCO);
        localStorage.removeItem(CLAVE_TOKEN_ACCESO_LEGADO);
        localStorage.removeItem(CLAVE_TOKEN_REFRESCO_LEGADO);
        // Redirigir al inicio de sesión solo si no estamos ya allí
        if (window.location.pathname !== '/iniciar-sesion') {
          window.location.href = '/iniciar-sesion';
        }
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
